import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ShoppingBag, ArrowLeft, CheckCircle, Loader2, CreditCard, Truck, Tag, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import AuthorizeNetCardForm from "@/components/AuthorizeNetCardForm";

var API = "/api";
var PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
var SHIPPING_COST = 12.50;

// Continental US states only — Alaska (AK), Hawaii (HI), and Washington D.C. (DC) are excluded.
var US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" }
];

function formatPrice(price) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
}

function ItemImage({ src }) {
  if (src) {
    return <img src={src} alt="" className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <ShoppingBag className="w-6 h-6 text-[#3f3f46]" />
    </div>
  );
}

function AddonLine({ addon }) {
  var text = "+ " + addon.quantity + "x " + addon.name;
  return <p className="text-[#A1A1AA] text-xs">{text}</p>;
}

function OrderSummaryItem({ item }) {
  var basePrice = item.sale_price || item.price;
  var addonTotal = 0;
  if (item.selectedAddons && item.selectedAddons.length > 0) {
    for (var i = 0; i < item.selectedAddons.length; i++) {
      addonTotal += item.selectedAddons[i].price * item.selectedAddons[i].quantity;
    }
  }
  var itemPrice = basePrice + addonTotal;
  var itemImg = item.images && item.images.length > 0 ? item.images[0] : null;
  
  return (
    <div className="flex gap-4">
      <div className="w-16 h-16 bg-[#1c1c1c] rounded-sm overflow-hidden">
        <ItemImage src={itemImg} />
      </div>
      <div className="flex-1">
        <p className="font-heading font-bold text-white text-sm uppercase line-clamp-1">{item.name}</p>
        {item.selectedAddons && item.selectedAddons.length > 0 && (
          <div className="mt-0.5">
            {item.selectedAddons.map(function(addon) {
              return <AddonLine key={addon.id} addon={addon} />;
            })}
          </div>
        )}
        <p className="text-[#A1A1AA] text-sm">Qty: {item.quantity}</p>
      </div>
      <p className="text-white font-heading font-bold">{formatPrice(itemPrice * item.quantity)}</p>
    </div>
  );
}

function OrderSummary({ items, subtotal, shipping, total, discount, couponCode, freeShipping }) {
  var hasCustomShipping = shipping !== 12.50;
  var hasDiscount = !!(discount && discount > 0);
  return (
    <div className="bg-[#121212] border border-white/5 rounded-sm p-6 sticky top-24">
      <h3 className="font-heading font-bold text-white text-xl uppercase mb-6">Order Summary</h3>
      <div className="space-y-4 mb-6">
        {items.map(function(item) {
          return <OrderSummaryItem key={item.cartItemId || item.id} item={item} />;
        })}
      </div>
      <div className="border-t border-white/10 pt-4 space-y-2">
        <div className="flex justify-between text-[#A1A1AA]">
          <span>Subtotal</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        {hasDiscount && (
          <div className="flex justify-between text-[#8CB369]" data-testid="order-discount-line">
            <span className="flex items-center gap-2">
              <Tag className="w-4 h-4" /> Discount{couponCode ? " (" + couponCode + ")" : ""}
            </span>
            <span>−{formatPrice(discount)}</span>
          </div>
        )}
        <div className="flex justify-between text-[#A1A1AA]">
          <span className="flex items-center gap-2">
            <Truck className="w-4 h-4" />{freeShipping ? "Shipping (Free)" : (hasCustomShipping ? "Shipping" : "USPS Priority Mail")}
          </span>
          <span>{formatPrice(shipping)}</span>
        </div>
        <div className="flex justify-between text-white font-heading font-bold text-xl pt-2 border-t border-white/10">
          <span>Total</span>
          <span className="text-[#C0A062]">{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type, required, testId }) {
  return (
    <div className="space-y-2">
      <Label className="text-[#A1A1AA] text-xs uppercase">{label}</Label>
      <Input 
        type={type || "text"} 
        value={value} 
        onChange={function(e) { onChange(e.target.value); }} 
        required={required} 
        className="input-tactical" 
        data-testid={testId} 
      />
    </div>
  );
}

function ContactForm({ name, setName, email, setEmail }) {
  return (
    <div>
      <h3 className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-4">Contact Information</h3>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name *" value={name} onChange={setName} required testId="checkout-name" />
        <FormField label="Email *" value={email} onChange={setEmail} type="email" required testId="checkout-email" />
      </div>
    </div>
  );
}

function ShippingForm({ address, setAddress, city, setCity, stateVal, setStateVal, zip, setZip }) {
  return (
    <div>
      <h3 className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-4">Shipping Address</h3>
      <p className="text-[#71717a] text-xs mb-4" data-testid="shipping-restriction-note">
        Shipping available to the Continental US only. We do not ship to Alaska, Hawaii, or Washington D.C.
      </p>
      <div className="space-y-4">
        <FormField label="Address" value={address} onChange={setAddress} testId="checkout-address" />
        <div className="grid grid-cols-2 gap-4">
          <FormField label="City" value={city} onChange={setCity} testId="checkout-city" />
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-xs uppercase">State *</Label>
            <select
              value={stateVal}
              onChange={function(e) { setStateVal(e.target.value); }}
              required
              className="input-tactical w-full h-10 px-3 bg-[#0a0a0a] border border-white/10 rounded-sm text-white text-sm focus:outline-none focus:border-[#C0A062]"
              data-testid="checkout-state"
            >
              <option value="">Select state…</option>
              {US_STATES.map(function(s) {
                return <option key={s.code} value={s.code}>{s.name} ({s.code})</option>;
              })}
            </select>
          </div>
        </div>
        <FormField label="ZIP Code" value={zip} onChange={setZip} testId="checkout-zip" />
      </div>
    </div>
  );
}

function EmptyCartView({ onShop }) {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container-tactical text-center py-24">
        <ShoppingBag className="w-16 h-16 text-[#3f3f46] mx-auto mb-4" />
        <h2 className="font-heading font-bold text-white text-2xl uppercase mb-4">Cart is Empty</h2>
        <Button className="btn-primary" onClick={onShop}>Continue Shopping</Button>
      </div>
    </div>
  );
}

function SuccessView({ orderId, onShop }) {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container-tactical">
        <div className="max-w-lg mx-auto text-center py-12 bg-[#121212] border border-white/5 rounded-sm p-8">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#4A5D23]/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-[#8CB369]" />
          </div>
          <h2 className="font-heading font-bold text-white text-3xl uppercase mb-4">Order Confirmed!</h2>
          <p className="text-[#A1A1AA] mb-2">Thank you for your purchase.</p>
          {orderId && <p className="text-[#C0A062] font-mono mb-6">Order ID: {orderId}</p>}
          <p className="text-[#A1A1AA] text-sm mb-8">A confirmation email has been sent.</p>
          <Button className="btn-primary" onClick={onShop}>Continue Shopping</Button>
        </div>
      </div>
    </div>
  );
}

function PaymentStep({ onBack, isProcessing, createOrder, onApprove, onError, paypalEnabled, creditcardEnabled, disabledMessage, anetPaymentIntentId, amount, customerName, onAnetSuccess, onAnetError, onCreateAnetIntent, creatingIntent }) {
  // Method picker — only relevant when BOTH are enabled. Default to credit card.
  var defaultMethod = creditcardEnabled ? "creditcard" : (paypalEnabled ? "paypal" : "none");
  var [method, setMethod] = useState(defaultMethod);
  // Re-sync default method if settings change
  useEffect(function () {
    setMethod(creditcardEnabled ? "creditcard" : (paypalEnabled ? "paypal" : "none"));
  }, [creditcardEnabled, paypalEnabled]);

  // Auto-create a server-side payment intent (Order) when user lands on card method
  useEffect(function () {
    if (method === "creditcard" && !anetPaymentIntentId && !creatingIntent) {
      onCreateAnetIntent && onCreateAnetIntent();
    }
  }, [method, anetPaymentIntentId, creatingIntent, onCreateAnetIntent]);

  var bothEnabled = creditcardEnabled && paypalEnabled;
  var noneEnabled = !creditcardEnabled && !paypalEnabled;

  return (
    <div>
      <button onClick={onBack} className="text-[#A1A1AA] hover:text-white text-sm mb-6">← Edit Information</button>
      <h3 className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-4">Payment</h3>

      {noneEnabled && (
        <div className="bg-[#121212] border border-red-500/30 rounded-sm p-6" data-testid="payments-disabled-notice">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              <h4 className="font-heading font-bold text-white text-base uppercase mb-2">Online Payments Temporarily Unavailable</h4>
              <p className="text-[#A1A1AA] text-sm whitespace-pre-line">{disabledMessage}</p>
              <a
                href="mailto:admin@resurrectedgunparts.com?subject=Order%20Inquiry"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#C0A062] text-black font-mono uppercase text-xs tracking-wider rounded-sm hover:bg-[#d4b675] transition-colors"
                data-testid="payments-disabled-contact"
              >
                Email Us To Order
              </a>
            </div>
          </div>
        </div>
      )}

      {!noneEnabled && bothEnabled && (
        <div className="flex gap-2 mb-4" data-testid="payment-method-picker">
          <button
            type="button"
            onClick={function () { setMethod("creditcard"); }}
            className={"flex-1 px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-wider border transition-colors " + (method === "creditcard" ? "bg-[#C0A062] text-black border-[#C0A062]" : "bg-[#121212] text-[#A1A1AA] border-white/10 hover:border-[#C0A062]/40")}
            data-testid="method-creditcard"
          >
            <CreditCard className="w-4 h-4 inline mr-1.5" /> Credit / Debit Card
          </button>
          <button
            type="button"
            onClick={function () { setMethod("paypal"); }}
            className={"flex-1 px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-wider border transition-colors " + (method === "paypal" ? "bg-[#C0A062] text-black border-[#C0A062]" : "bg-[#121212] text-[#A1A1AA] border-white/10 hover:border-[#C0A062]/40")}
            data-testid="method-paypal"
          >
            PayPal / Venmo
          </button>
        </div>
      )}

      {!noneEnabled && method === "creditcard" && (
        isProcessing || creatingIntent ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" />
            <span className="ml-3 text-[#A1A1AA]">{creatingIntent ? "Preparing your order..." : "Processing payment..."}</span>
          </div>
        ) : (
          <AuthorizeNetCardForm
            paymentIntentId={anetPaymentIntentId}
            amount={amount}
            customerName={customerName}
            onSuccess={onAnetSuccess}
            onError={onAnetError}
          />
        )
      )}

      {!noneEnabled && method === "paypal" && (
        isProcessing ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" />
            <span className="ml-3 text-[#A1A1AA]">Processing payment...</span>
          </div>
        ) : (
          <div className="bg-[#121212] border border-white/5 rounded-sm p-6" data-testid="paypal-buttons-container">
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", "enable-funding": "venmo" }}>
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "rect" }}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={onError}
              />
            </PayPalScriptProvider>
            <p className="text-center text-[#71717a] text-xs mt-4">PayPal & Venmo accepted</p>
          </div>
        )
      )}
    </div>
  );
}

export default function CheckoutPage() {
  var navigate = useNavigate();
  var searchParamsArr = useSearchParams();
  var searchParams = searchParamsArr[0];
  var cart = useCart();
  var auth = useAuth();
  var items = cart.cartItems;
  var itemCount = items.length;
  var cartSubtotal = cart.getCartTotal();
  var [shippingCost, setShippingCost] = useState(SHIPPING_COST);
  var [paypalEnabled, setPaypalEnabled] = useState(false);
  var [creditcardEnabled, setCreditcardEnabled] = useState(true);
  var [paypalDisabledMessage, setPaypalDisabledMessage] = useState("");
  var [anetPaymentIntentId, setAnetPaymentIntentId] = useState(null);
  var [creatingIntent, setCreatingIntent] = useState(false);

  // Coupon state
  var [couponInput, setCouponInput] = useState("");
  var [appliedCoupon, setAppliedCoupon] = useState(null);   // {code, discount, free_shipping, discount_type, discount_value}
  var [couponError, setCouponError] = useState("");
  var [applyingCoupon, setApplyingCoupon] = useState(false);

  var effectiveShipping = appliedCoupon && appliedCoupon.free_shipping ? 0 : shippingCost;
  var discount = appliedCoupon ? (appliedCoupon.discount || 0) : 0;
  var orderTotal = Math.max(0, cartSubtotal - discount + effectiveShipping);
  
  var [step, setStep] = useState("info");
  var [isProcessing, setIsProcessing] = useState(false);
  var [orderId, setOrderId] = useState(null);
  var [name, setName] = useState(auth.user ? auth.user.name : "");
  var [email, setEmail] = useState(auth.user ? auth.user.email : "");
  var [address, setAddress] = useState("");
  var [city, setCity] = useState("");
  var [stateVal, setStateVal] = useState("");
  var [zip, setZip] = useState("");

  // Live shipping quote whenever the cart contents change.
  useEffect(function fetchShipping() {
    if (!items || items.length === 0) {
      setShippingCost(SHIPPING_COST);
      return;
    }
    var quoteItems = items.map(function(it) { return { product_id: it.id, quantity: it.quantity }; });
    axios.post(API + "/shipping/quote", { items: quoteItems })
      .then(function(res) {
        if (res.data && typeof res.data.shipping === "number") {
          setShippingCost(res.data.shipping);
        }
      })
      .catch(function() { setShippingCost(SHIPPING_COST); });
  }, [items]);

  // Fetch payment availability once on mount
  useEffect(function fetchPaymentStatus() {
    axios.get(API + "/site-settings/payments")
      .then(function(res) {
        setPaypalEnabled(res.data.paypal_enabled === true);
        setCreditcardEnabled(res.data.creditcard_enabled !== false);
        setPaypalDisabledMessage(res.data.disabled_message || "");
      })
      .catch(function() {});
  }, []);

  useEffect(function checkPayPalReturn() {
    var token = searchParams.get("token");
    if (token) {
      setIsProcessing(true);
      axios.post(API + "/paypal/capture-order", { order_id: token })
        .then(function(res) {
          if (res.data.order) { setOrderId(res.data.order.order_id); }
          setStep("success");
          cart.clearCart();
          toast.success("Payment successful!");
        })
        .catch(function() { toast.error("Payment failed"); setStep("payment"); })
        .finally(function() { setIsProcessing(false); });
    }
  }, []);

  function goToShop() { navigate("/shop"); }
  function goBackToInfo() { setStep("info"); }

  // ----- Coupon handlers -----
  function buildOrderItemsForServer() {
    var orderItems = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var img = item.images && item.images.length > 0 ? item.images[0] : null;
      var basePrice = item.sale_price || item.price;
      var addonsData = [];
      if (item.selectedAddons && item.selectedAddons.length > 0) {
        for (var j = 0; j < item.selectedAddons.length; j++) {
          var addon = item.selectedAddons[j];
          addonsData.push({ id: addon.id, name: addon.name, price: addon.price, quantity: addon.quantity });
        }
      }
      orderItems.push({
        product_id: item.id,
        name: item.name,
        price: basePrice,
        quantity: item.quantity,
        image: img,
        addons: addonsData
      });
    }
    return orderItems;
  }

  function applyCoupon() {
    var code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError("Enter a code to apply."); return; }
    if (applyingCoupon) return;
    setApplyingCoupon(true);
    setCouponError("");
    axios.post(API + "/coupons/validate", {
      code: code,
      items: buildOrderItemsForServer(),
      user_email: email || null
    })
      .then(function (res) {
        var data = res.data;
        if (data.ok) {
          setAppliedCoupon(data);
          // If user had a created Anet intent, force re-creating it so the discount is applied server-side
          setAnetPaymentIntentId(null);
          toast.success("Coupon applied: " + data.code);
        } else {
          setCouponError(data.error || "Coupon could not be applied.");
        }
      })
      .catch(function () { setCouponError("Could not check that coupon. Try again."); })
      .finally(function () { setApplyingCoupon(false); });
  }

  function removeCoupon() {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
    // Force fresh intent so the next charge does not carry the old discount
    setAnetPaymentIntentId(null);
  }

  function handleContinueToPayment(e) {
    e.preventDefault();
    if (!name || !email) { toast.error("Please fill in required fields"); return; }
    if (!address || !city || !stateVal || !zip) {
      toast.error("Please complete your shipping address");
      return;
    }
    var restricted = ["AK", "HI", "DC"];
    if (restricted.indexOf((stateVal || "").toUpperCase()) !== -1) {
      toast.error("We ship to the Continental US only — Alaska, Hawaii, and D.C. are not supported.");
      return;
    }
    setStep("payment");
  }

  function createPayPalOrder() {
    var orderItems = [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var img = item.images && item.images.length > 0 ? item.images[0] : null;
      var basePrice = item.sale_price || item.price;
      var addonsData = [];
      if (item.selectedAddons && item.selectedAddons.length > 0) {
        for (var j = 0; j < item.selectedAddons.length; j++) {
          var addon = item.selectedAddons[j];
          addonsData.push({ id: addon.id, name: addon.name, price: addon.price, quantity: addon.quantity });
        }
      }
      orderItems.push({ 
        product_id: item.id, 
        name: item.name, 
        price: basePrice,
        quantity: item.quantity, 
        image: img,
        addons: addonsData
      });
    }
    return axios.post(API + "/paypal/create-order", {
      items: orderItems, 
      user_email: email, 
      user_name: name,
      shipping_address: { address: address, city: city, state: stateVal, zip: zip, country: "USA" },
      shipping_cost: shippingCost
    }).then(function(res) { return res.data.id; });
  }

  function onPayPalApprove(data) {
    setIsProcessing(true);
    axios.post(API + "/paypal/capture-order", { order_id: data.orderID })
      .then(function(res) {
        if (res.data.order) { setOrderId(res.data.order.order_id); }
        setStep("success");
        cart.clearCart();
        toast.success("Payment successful!");
      })
      .catch(function() { toast.error("Payment capture failed"); })
      .finally(function() { setIsProcessing(false); });
  }

  function onPayPalError(err) { 
    toast.error("PayPal error"); 
    console.error(err); 
  }

  // ----- Authorize.Net handlers -----
  function createAnetIntent() {
    if (creatingIntent || anetPaymentIntentId) return;
    if (!name || !email) {
      toast.error("Please complete contact information first");
      setStep("info");
      return;
    }
    setCreatingIntent(true);
    axios.post(API + "/anet/create-order", {
      items: buildOrderItemsForServer(),
      user_email: email,
      user_name: name,
      shipping_address: { address: address, city: city, state: stateVal, zip: zip, country: "USA" },
      shipping_cost: shippingCost,
      coupon_code: appliedCoupon ? appliedCoupon.code : null
    })
      .then(function (res) {
        setAnetPaymentIntentId(res.data.id);
      })
      .catch(function (err) {
        toast.error(err.response?.data?.detail || "Could not prepare your order. Please try again.");
      })
      .finally(function () { setCreatingIntent(false); });
  }

  function onAnetSuccess(data) {
    if (data && data.order) {
      setOrderId(data.order.order_id);
    }
    setStep("success");
    cart.clearCart();
    toast.success("Payment successful!");
  }

  function onAnetError(err) {
    // Error already shown by the form; no need for additional toast unless network failure.
    console.error("Authorize.Net error:", err);
  }

  if (itemCount === 0 && step !== "success") {
    return <EmptyCartView onShop={goToShop} />;
  }

  if (step === "success") {
    return <SuccessView orderId={orderId} onShop={goToShop} />;
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="checkout-page">
      <div className="container-tactical">
        <button onClick={goToShop} className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#C0A062] mb-8">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm uppercase">Back to Shop</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h1 className="font-heading font-black text-4xl text-white uppercase mb-8">Checkout</h1>
            
            {step === "info" && (
              <form onSubmit={handleContinueToPayment} className="space-y-6" data-testid="checkout-form">
                <ContactForm name={name} setName={setName} email={email} setEmail={setEmail} />
                <ShippingForm 
                  address={address} setAddress={setAddress}
                  city={city} setCity={setCity}
                  stateVal={stateVal} setStateVal={setStateVal}
                  zip={zip} setZip={setZip}
                />
                <Button type="submit" className="btn-primary w-full" data-testid="continue-payment-btn">
                  <CreditCard className="w-4 h-4 mr-2" />Continue to Payment
                </Button>
              </form>
            )}

            {step === "payment" && (
              <PaymentStep
                onBack={goBackToInfo}
                isProcessing={isProcessing}
                createOrder={createPayPalOrder}
                onApprove={onPayPalApprove}
                onError={onPayPalError}
                paypalEnabled={paypalEnabled}
                creditcardEnabled={creditcardEnabled}
                disabledMessage={paypalDisabledMessage}
                anetPaymentIntentId={anetPaymentIntentId}
                amount={orderTotal}
                customerName={name}
                onAnetSuccess={onAnetSuccess}
                onAnetError={onAnetError}
                onCreateAnetIntent={createAnetIntent}
                creatingIntent={creatingIntent}
              />
            )}
          </div>

          <div>
            <OrderSummary
              items={items}
              subtotal={cartSubtotal}
              shipping={effectiveShipping}
              total={orderTotal}
              discount={discount}
              couponCode={appliedCoupon ? appliedCoupon.code : null}
              freeShipping={appliedCoupon && appliedCoupon.free_shipping}
            />

            {/* Coupon code input */}
            <div className="bg-[#121212] border border-white/5 rounded-sm p-4 mt-4" data-testid="coupon-input-section">
              {appliedCoupon ? (
                <div className="flex items-center justify-between" data-testid="applied-coupon">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#8CB369]" />
                    <div>
                      <p className="font-mono text-sm text-[#8CB369] font-bold">{appliedCoupon.code}</p>
                      <p className="text-[10px] text-[#71717a]">
                        {appliedCoupon.discount_type === "percentage"
                          ? appliedCoupon.discount_value + "% off"
                          : "$" + parseFloat(appliedCoupon.discount_value).toFixed(2) + " off"}
                        {appliedCoupon.free_shipping ? " · Free shipping" : ""}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="text-[#71717a] hover:text-red-400"
                    data-testid="remove-coupon-btn"
                    aria-label="Remove coupon"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <Label className="text-[#A1A1AA] text-xs uppercase mb-2 block">Have a Coupon Code?</Label>
                  <div className="flex gap-2">
                    <Input
                      value={couponInput}
                      onChange={function (e) { setCouponInput(e.target.value.toUpperCase()); }}
                      onKeyDown={function (e) { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                      placeholder="ENTER CODE"
                      className="input-tactical font-mono"
                      data-testid="coupon-input"
                    />
                    <Button
                      type="button"
                      className="btn-primary"
                      onClick={applyCoupon}
                      disabled={applyingCoupon || !couponInput.trim()}
                      data-testid="apply-coupon-btn"
                    >
                      {applyingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {couponError && (
                    <p className="text-red-400 text-xs mt-2" data-testid="coupon-error">{couponError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
